import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { axiosClient } from '../../library/Service/axiosClient';
import UnivarietyLogo from '../../styles/images/logo.png';
import './feedback.css';
import RenderQuestion from './components/RenderQuestion';
import axios from 'axios';
import Swal from 'sweetalert2';
import { isEmpty } from '../../library/Core/SmartFunctions'; 

interface FeedbackFormData {
  feedback_form: [{
    id: string;
    title: string;
    description: string;
    related_feature: string;
    status: string;
    responded_at: string | null;
  }];
  questions: [{
    id: number;
    question: string;
    type: 'rating' | 'free_text' | 'radio' | 'checkbox';
    options: string[] | null;
    is_required: number;
    answer?: string;
  }];
}

const errConfig: Record<string, {
    title: string;
    message: string;
    icon: 'warning' | 'error' | 'success' | 'info' | 'question';
    messageImgClass: string;
}> = {
    "ERR_NETWORK": {
        "title": "Network Error",
        "message": "Please check your internet connection.",
        "icon": "error",
        "messageImgClass": "bi-wifi-off text-danger"
    },
    "OFFLINE": {
        "title": "You're Offline",
        "message": "Please check your internet connection and try again.",
        "icon": "warning",
        "messageImgClass": "bi-wifi-off text-warning"
    }
};

const showLoader = (title?: string, html?: string) => {
  return Swal.fire({
    title: `${title}` || 'Loading...',
    html: `${html}` || 'Please wait!<br/><br/><br/><br/>',
    allowOutsideClick: false,
    didOpen: () => {
      Swal.showLoading();
    },
  });
};

const RenderFeedbackForm: React.FC = () => {
  const { id, userId, loadType } = useParams();
  const [formData, setFormData] = useState<FeedbackFormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [responses, setResponses] = useState<Record<number, string | number>>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [formSubmitSuccess, setFormSubmitSuccess] = useState(false);
  const [message, setMessage] = useState('');
  const [messageTitle, setMessageTitle] = useState('Thank You!!');
//   const [messageImg, setMessageImg] = useState('check_tick.svg');
  const [messageImgClass, setMessageImgClass] = useState('bi-patch-check-fill text-green');
  const [loaderTitle, setLoaderTitle] = useState('Loading...');
  const [loaderHtml, setLoaderHtml] = useState('');
  // Call loader immediately when component mounts
  useEffect(() => {
    showLoader();
  }, []);

  useEffect(() => {
    if (loading) {
        showLoader(loaderTitle, loaderHtml);
    } else {
        Swal.close();
    }
  }, [loading]);

  useEffect(() => {
    const fetchFeedbackForm = async () => {
      try {
        setLoading(true);
        const response = await axiosClient().get(
          `${process.env.REACT_APP_CRM_API}/get/feedback-form-config/${id}/${userId}`
        );
        
        setFormData(response.data.data);
        
        // If in preview mode and answers exist, populate the responses
        if (loadType === 'preview' && response.data.data.questions) {
          const initialResponses: Record<number, string | number> = {};
          response.data.data.questions.forEach((question: any) => {
            if (question.answer !== undefined && question.answer !== null) {
              initialResponses[question.id] = question.answer;
            }
          });
          setResponses(initialResponses);
        }
        
        setFormSubmitSuccess(false);
        setError(null);
      } catch (err) {
        console.error('Error fetching feedback form:', err);
        setError('Failed to load feedback form. Please try again later.');
        setFormSubmitSuccess(true);
      } finally {
        setLoading(false);
      }
    };

    if (id && userId) {
      fetchFeedbackForm();
    }
  }, [id, userId, loadType]);

  useEffect(() => {
    
  if (formData?.feedback_form === undefined || formData?.feedback_form?.length <= 0) {
        setMessageTitle('Oops!');
        setMessageImgClass("bi-patch-exclamation-fill text-danger");
        setMessage("Something went wrong, please contact administrator.");
        setFormSubmitSuccess(true);
    } else if (loadType !== 'preview' && !isEmpty(formData?.feedback_form?.[0]?.['responded_at'])) {
        setMessageTitle('Thank You!!');
        setMessageImgClass("bi-patch-check-fill text-green");
        setMessage('You have already submitted the feedback form.');
        setFormSubmitSuccess(true);
    } else if (loadType !== 'preview' && !isEmpty(formData?.feedback_form?.[0]?.['status']) && formData?.feedback_form[0]['status'].toLocaleLowerCase() !== 'published') {
        setMessageTitle('Oops!');
        setMessage('This form is no longer accepting responses, Please contact administrator.');
        setFormSubmitSuccess(true);
    }
  }, [formData]);

  const handleResponseChange = React.useCallback((questionId: number, value: string | number) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  }, []);

  const validateForm = (): boolean => {
    let isValid = true;
    
    formData?.questions?.forEach(question => {
      if (question.is_required === 1) {
        const response = responses[question.id];
        
        if (response === undefined || 
            response === null || 
            (typeof response === 'string' && response.trim() === '') ||
            (typeof response === 'number' && response === 0)) {
          isValid = false;
          
          // Find and focus the first invalid required field
          const questionElement = document.querySelector(`[data-question-id="${question.id}"]`);
          if (questionElement) {
            const input = questionElement.querySelector('input, textarea, .rating-container');
            if (input) {
              questionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              setTimeout(() => {
                (input as HTMLElement).focus();
              }, 100);
            }
            return false; // Exit forEach early once we find first error
          }
        }
      }
    });
    
    return isValid;
};

const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasSubmitted(true);
    
    if (!validateForm()) {
      return;
    }

    setLoaderHtml('Please wait while we submit your feedback!<br/><br/><br/><br/>');
    setLoading(true);
    try {
      // First get CSRF cookie
      try {
        await fetch(`${process.env.REACT_APP_CRM_API}/sanctum/csrf-cookie`, {
          credentials: 'include'
        });
      } catch (csrfError) {
        setLoading(false);
        console.error('CSRF cookie fetch failed:', csrfError);
      }

      // Get CSRF token from cookie
      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      const payload = {
        formId: id,
        userId: userId,
        responses: Object.entries(responses).map(([questionId, value]) => ({
          questionId: Number(questionId),
          response: value
        }))
      };

      // Add delay to ensure preflight happens first
      await new Promise(resolve => setTimeout(resolve, 100));

      const response = await axios.post(
        `${process.env.REACT_APP_CRM_API}/submit-feedback`, 
        payload, 
        {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-XSRF-TOKEN': decodeURIComponent(xsrfToken || ''),
          },
          withCredentials: true,
        }
      );

      if (response.data.status === 'success') {
        setError(null);
        setMessageImgClass('bi-patch-check-fill text-green');
        setMessageTitle('Thank You!!');
        setMessage(response.data.message);
        setFormSubmitSuccess(true);
      } else {
        setMessageImgClass('bi-patch-exclamation-fill text-danger');
        setMessageTitle('Oops!');
        setMessage(response.data.message);
        setFormSubmitSuccess(true);
      }
      setLoading(false);
    } catch (err: any) {
      console.error('Error submitting feedback:', err);
      const errorMessage = err?.message || 'Failed to submit feedback. Please try again.';
      setError(errorMessage);
      setLoading(false);
      Swal.fire({
        title: `${errConfig?.[err?.code]?.title || 'Error'}`,
        text: `${errConfig?.[err?.code]?.message || errorMessage}`,
        icon: `${errConfig?.[err?.code]?.icon || 'error'}`,
        confirmButtonText: 'OK'
      });
      setMessageImgClass(errConfig?.[err?.code]?.messageImgClass || 'bi-patch-exclamation-fill text-danger');
      setMessageTitle(errConfig?.[err?.code]?.title || 'Oops!');
      setMessage(errConfig?.[err?.code]?.message || errorMessage);
      
      setFormSubmitSuccess(true);
    }
};

if (!formSubmitSuccess && error) {
    return <div className="error-message">{error}</div>;
}

//   if (!formData) {
//     return <div>No form data available</div>;
//   }

  if (formSubmitSuccess && !loading) {
    return (
        <div className="modal fade show" id="confirm_pop" tabIndex={-1} role="dialog" aria-labelledby="exampleModalLabelvideo" aria-modal="true" style={{ display: 'block' }}>
        <div className="strip">
            <i></i><i></i><i></i><i></i><i></i>
        </div>
        <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
                <div className="modal-body">
                    <div className="thank-you bg-white radius-4 text-center">
                        <div className="check mt-5">
                            {/* <img src={`${process.env.REACT_APP_IMAGE_BASEPATH}/${messageImg}`} width="60" height="auto" className="img-fluid"/> */}
                            <i className={`bi ${messageImgClass}`} style={{ fontSize: "60px", lineHeight: "60px" }}></i>
                        </div>
                        <h3 className="font-36 font-900 mt-2 mb-2 text-black">{messageTitle}</h3>
                        <h4 className="font-16 font-400 mb-5 text-center">{message}</h4>
                    </div>

                </div>
            </div>
        </div>
    </div>
    );
  }

  const renderQuestions = () => {
    const isPreviewMode: boolean = Boolean(loadType === 'preview' && formData?.feedback_form?.[0]?.responded_at);
    
    return formData?.questions?.map((question) => (
      <div key={question.id} data-question-id={question.id}>
        <RenderQuestion
          question={question}
          onChange={handleResponseChange}
          responses={responses}
          showValidation={hasSubmitted}
          disabled={isPreviewMode}
        />
      </div>
    ));
  };

  if (loading) {
    return (<div></div>);
  }
  
  return (
    <div className={`feedback-form-container overflow-visible formtype-${loadType || 'default'}`}>
      <div className="main-wrapper py-5 h-auto">
        <div className="container">
          <form onSubmit={handleSubmit} noValidate>
            <div className="mx-auto text-center pb-4">
              <img src={UnivarietyLogo} alt="Univariety Logo" className="logo-img" />
            </div>
            <div className="max-700 mx-auto">
              <div className="feedback-card p-0 mb-5">
                {formData?.feedback_form?.[0] && (
                //   <div className="form-header mb-4">
                //     <h2 className="form-title">{formData.feedback_form[0].title}</h2>
                //     {formData.feedback_form[0].description && formData.feedback_form[0].description !== 'NA' && (
                //       <p className="form-description text-start">
                //         {formData.feedback_form[0].description.split('\r\n').map((line, i) => (
                //           <React.Fragment key={i}>
                //             {line}
                //             <br />
                //           </React.Fragment>
                //         ))}
                //       </p>
                //     )}
                //   </div>
                <div className="experienceboxheading mb-0">
                    <h3>{formData.feedback_form[0].title}</h3>
                    {formData.feedback_form[0].description && formData.feedback_form[0].description !== 'NA' && (
                        <p>
                        {formData.feedback_form[0].description.split('\r\n').map((line, i) => (
                            <React.Fragment key={i}>
                                {line}
                                <br />
                            </React.Fragment>
                        ))}
                        </p>
                    )}
                </div>
                )}
                
                <div className="questions-container p-4">
                  {renderQuestions()}
                </div>

                {(!loadType || loadType !== 'preview' || !formData?.feedback_form?.[0]?.responded_at) && (
                  <div className="submit-section pb-4">
                    <button type="submit" className="btn btn-green mx-auto">
                      <span>Submit Feedback</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RenderFeedbackForm;